import { Injectable, OnDestroy } from '@angular/core';
import { ActivityLoggerService } from './activityLogger.service';

@Injectable()
export class AutoRefreshService implements OnDestroy {
  private refreshTimeoutId?: number;
  private checkIntervalId?: number;
  
  private readonly TARGET_HOUR = 1; // 1h du matin
  private readonly CHECK_INTERVAL_MS = 60000; // Vérifier toutes les minutes
  
  constructor(private logService: ActivityLoggerService) {}

  /**
   * Démarre le système de rafraîchissement automatique
   */
  public startAutoRefresh(): void {
    this.scheduleNextRefresh();
    this.startPeriodicCheck();
    this.logService.logActivity('auto_refresh_started', 'Auto-refresh scheduled for 1:00 AM');
  }

  /**
   * Planifie le prochain rafraîchissement à 1h du matin
   */
  private scheduleNextRefresh(): void {
    const now = new Date();
    const nextRefresh = this.calculateNextRefreshTime(now);
    const delay = nextRefresh.getTime() - now.getTime();

    this.clearRefreshTimeout();
    
    this.refreshTimeoutId = window.setTimeout(() => {
      this.performRefresh();
    }, delay);

    this.logService.logActivity(
      'refresh_scheduled', 
      `Next refresh at: ${nextRefresh.toLocaleString('fr-FR')}`
    );
  }

  /**
   * Calcule l'heure du prochain rafraîchissement (1h du matin)
   */
  private calculateNextRefreshTime(now: Date): Date {
    const nextRefresh = new Date(now);
    nextRefresh.setHours(this.TARGET_HOUR, 0, 0, 0);

    // Si 1h du matin est déjà passé aujourd'hui, planifier pour demain
    if (nextRefresh.getTime() <= now.getTime()) {
      nextRefresh.setDate(nextRefresh.getDate() + 1);
    }

    return nextRefresh;
  }

  /**
   * Démarre une vérification périodique pour s'assurer que le rafraîchissement fonctionne
   * Utile si l'appareil était éteint à 1h du matin
   */
  private startPeriodicCheck(): void {
    this.clearCheckInterval();
    
    this.checkIntervalId = window.setInterval(() => {
      const now = new Date();
      const lastRefresh = this.getLastRefreshDate();
      
      // Si aucun rafraîchissement depuis plus de 25h, forcer le rafraîchissement
      if (this.shouldForceRefresh(now, lastRefresh)) {
        this.logService.logActivity('forced_refresh', 'Forcing refresh due to missed schedule');
        this.performRefresh();
      }
    }, this.CHECK_INTERVAL_MS);
  }

  /**
   * Détermine si un rafraîchissement forcé est nécessaire
   */
  private shouldForceRefresh(now: Date, lastRefresh: Date | null): boolean {
    if (!lastRefresh) {
      return false;
    }

    const hoursSinceRefresh = (now.getTime() - lastRefresh.getTime()) / (1000 * 60 * 60);
    return hoursSinceRefresh > 25;
  }

  /**
   * Récupère la date du dernier rafraîchissement depuis localStorage
   */
  private getLastRefreshDate(): Date | null {
    const lastRefreshStr = localStorage.getItem('last_app_refresh');
    return lastRefreshStr ? new Date(lastRefreshStr) : null;
  }

  /**
   * Enregistre la date du rafraîchissement dans localStorage
   */
  private saveLastRefreshDate(): void {
    localStorage.setItem('last_app_refresh', new Date().toISOString());
  }

  /**
   * Effectue le rafraîchissement de l'application
   * Note: window.location.reload() détruit complètement l'application Angular
   * et réinitialise le contexte JavaScript. Toutes les subscriptions, timers
   * et observables sont automatiquement nettoyés par le navigateur.
   */
  private performRefresh(): void {
    this.logService.logActivity('app_refresh', 'Refreshing application at 1:00 AM');
    this.saveLastRefreshDate();
    window.location.reload();
  }

  /**
   * Nettoie le timeout de rafraîchissement
   */
  private clearRefreshTimeout(): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = undefined;
    }
  }

  /**
   * Nettoie l'intervalle de vérification
   */
  private clearCheckInterval(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = undefined;
    }
  }

  /**
   * Arrête le système de rafraîchissement automatique
   */
  public stopAutoRefresh(): void {
    this.clearRefreshTimeout();
    this.clearCheckInterval();
    this.logService.logActivity('auto_refresh_stopped', 'Auto-refresh stopped');
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }
}
